import { ArrowDropDown, ScienceOutlined, Search, WarningAmber } from '@mui/icons-material';
import { MoftakiBotIcon } from '../shared/MoftakiBotIcon';
import {
  Alert,
  AppBar,
  Avatar,
  Box,
  Chip,
  ClickAwayListener,
  Collapse,
  Divider,
  InputAdornment,
  Menu,
  MenuItem,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../../auth/useAuth';
import { APP_VERSION } from '../../constants/changelog';
import { APP_NAME } from '../../constants/config';
import { onServiceStatus } from '../../services/apiClient';
import { ROUTES } from '../../constants/routes';
import { searchAll } from '../../services/searchService';
import type { SearchResults } from '../../services/searchService';
import { ChangelogDialog } from './ChangelogDialog';
import { MobileBottomNav, MOBILE_BOTTOM_NAV_HEIGHT } from './MobileBottomNav';
import { SplashScreen } from './SplashScreen';
import { FeedbackFab } from './FeedbackFab';
import { SearchDropdown } from './SearchDropdown';
import { useActivityTracker } from '../../hooks/useActivityTracker';
import { useImpersonation } from '../../hooks/useImpersonation';
import { usePermissions, type AppRole } from '../../hooks/usePermissions';
import { useGuardedNavigate } from '../../hooks/useUnsavedGuard';
import { GuardedLink } from '../shared/GuardedLink';

export const APP_BAR_HEIGHT = 60;

export const AppShell = () => {
  const { user, logout } = useAuth();
  const { impersonatingAs, startImpersonation, stopImpersonation, isImpersonating } = useImpersonation();
  // isAdmin follows the "View as role" preview (nav matches the previewed role);
  // isSuperAdmin reflects the ACTUAL role so the impersonation menu never disappears.
  const { isAdmin, isSuperAdmin } = usePermissions();
  const [serviceDown, setServiceDown] = useState<{ unavailable: boolean; message: string }>({ unavailable: false, message: '' });
  // Below md the top nav + search collapse and MobileBottomNav takes over
  // (mockups 2026-07-24: Dashboard → My Tests → test in two taps).
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));

  // Activity tracking — logs page visits + login events to backend for analytics
  useActivityTracker();

  useEffect(() => {
    onServiceStatus((status) => setServiceDown(status));
    return () => onServiceStatus(null);
  }, []);
  const userName = user?.displayName ?? 'User';
  const userEmail = user?.email ?? '';
  const userInitials = useMemo(() => {
    if (!user?.displayName) return '?';
    const parts = user.displayName.split(' ').filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    return parts[0][0].toUpperCase();
  }, [user?.displayName]);
  // BUG-2026-0189 — route every in-app exit surface through the unsaved-changes
  // guard so leaving a dirty test prompts Save & Leave / Discard & Leave / Cancel.
  const guardedNavigate = useGuardedNavigate();
  const location = useLocation();
  const [searchValue, setSearchValue] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResults | null>(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [changelogOpen, setChangelogOpen] = useState(false);
  const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(null);
  const [dirMenuAnchor, setDirMenuAnchor] = useState<null | HTMLElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const handleSearchChange = (val: string) => {
    setSearchValue(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (val.trim().length < 2) { setSearchOpen(false); return; }
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await searchAll(val.trim());
        setSearchResults(res);
        setSearchOpen(true);
      } catch { /* ignore */ }
    }, 300);
  };

  // Daily lab-work flow: a Project holds Work Orders, which hold Tests.
  const navItems = [
    { label: 'Projects', path: ROUTES.PROJECTS },
    { label: 'Work Orders', path: ROUTES.WORK_ORDERS_TAB },
    { label: 'Tests', path: ROUTES.TESTS },
  ];

  // Planning group — Quotes bring work in, Schedule plans it —
  // set apart from the daily-work group by dividers.
  const workIntakeItems = [
    { label: 'Quotes', path: ROUTES.QUOTES },
    { label: 'Schedule', path: ROUTES.SCHEDULE },
  ];

  // Reference registries live under the Directory dropdown; each is also reachable
  // through its parent detail page (Client → Sites, Site → Locations, Project → Samples).
  const directoryItems = [
    { label: 'Clients', path: ROUTES.CLIENTS },
    { label: 'Sites', path: ROUTES.SITES },
    { label: 'Locations', path: ROUTES.LOCATIONS_MANAGE },
    { label: 'Samples', path: ROUTES.SAMPLES },
  ];
  const isDirectoryActive = directoryItems.some((item) => location.pathname.startsWith(item.path));

  const renderNavLink = (item: { label: string; path: string }) => {
    const isActive = item.path === ROUTES.WORK_ORDERS_TAB
      ? location.pathname === ROUTES.WORK_ORDERS_TAB || location.pathname.includes('/workorders/')
      : location.pathname.startsWith(item.path) && !location.pathname.includes('/workorders/');
    return (
      <GuardedLink key={item.path} to={item.path} style={{ textDecoration: 'none' }}>
        <Typography
          variant="body2"
          sx={{
            px: 1.5,
            py: 0.5,
            borderRadius: '6px',
            fontSize: '0.8rem',
            fontWeight: isActive ? 600 : 400,
            color: isActive ? '#FFFFFF' : alpha('#FFFFFF', 0.6),
            backgroundColor: isActive ? alpha('#FFFFFF', 0.12) : 'transparent',
            transition: 'all 0.2s ease',
            '&:hover': {
              backgroundColor: alpha('#FFFFFF', 0.1),
              color: '#FFFFFF',
            },
          }}
        >
          {item.label}
        </Typography>
      </GuardedLink>
    );
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && searchValue.trim()) {
      guardedNavigate(`${ROUTES.PROJECTS}?search=${encodeURIComponent(searchValue.trim())}`);
    }
  };

  // Ctrl+K keyboard shortcut to focus search
  const handleGlobalKeyDown = useCallback((e: KeyboardEvent) => {
    if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
      e.preventDefault();
      searchRef.current?.focus();
    }
  }, []);

  useEffect(() => {
    window.addEventListener('keydown', handleGlobalKeyDown);
    return () => window.removeEventListener('keydown', handleGlobalKeyDown);
  }, [handleGlobalKeyDown]);

  // Clear search on navigation
  useEffect(() => {
    setSearchValue('');
    setSearchOpen(false);
  }, [location.pathname]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <AppBar position="fixed" elevation={0}
        sx={{ zIndex: (t) => t.zIndex.drawer + 1, background: '#3E4A3E',
          borderBottom: `1px solid ${alpha('#8CA882', 0.15)}`, boxShadow: `0 1px 8px ${alpha('#1A231A', 0.25)}`,
        }}>
        <Toolbar sx={{ justifyContent: 'space-between', minHeight: `${APP_BAR_HEIGHT}px !important`, height: APP_BAR_HEIGHT, px: 2 }}>
          {/* Left: Logo + Version + Nav */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mr: 0.5 }}>
              <GuardedLink to={ROUTES.DASHBOARD} style={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, height: 38, color: alpha('#FFFFFF', 0.92) }}>
                  <ScienceOutlined sx={{ fontSize: 30 }} />
                  <Typography component="span" sx={{ fontWeight: 800, fontSize: '1.7rem', letterSpacing: '0.12em', lineHeight: 1 }}>
                    {APP_NAME}
                  </Typography>
                </Box>
              </GuardedLink>
              <Chip label={APP_VERSION} size="small" onClick={() => setChangelogOpen(true)}
                sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700, mt: 0, backgroundColor: alpha('#A8D99C', 0.35),
                  color: '#FFFFFF', border: `1px solid ${alpha('#A8D99C', 0.5)}`, cursor: 'pointer', borderRadius: 1,
                  transition: 'all 0.2s ease', '&:hover': { backgroundColor: alpha('#A8D99C', 0.55) } }} />
            </Box>
            {/* Nav links (desktop only — MobileBottomNav covers small screens) */}
            <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 0.5, ml: 2 }}>
              {navItems.map(renderNavLink)}
              <Box sx={{ width: '1px', height: 18, alignSelf: 'center', mx: 0.5, backgroundColor: alpha('#FFFFFF', 0.2) }} />
              {workIntakeItems.map(renderNavLink)}
              <Box sx={{ width: '1px', height: 18, alignSelf: 'center', mx: 0.5, backgroundColor: alpha('#FFFFFF', 0.2) }} />
              <Typography
                variant="body2"
                onClick={(e) => setDirMenuAnchor(e.currentTarget)}
                sx={{
                  px: 1.5,
                  py: 0.5,
                  borderRadius: '6px',
                  fontSize: '0.8rem',
                  fontWeight: isDirectoryActive ? 600 : 400,
                  color: isDirectoryActive ? '#FFFFFF' : alpha('#FFFFFF', 0.6),
                  backgroundColor: isDirectoryActive ? alpha('#FFFFFF', 0.12) : 'transparent',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: alpha('#FFFFFF', 0.1),
                    color: '#FFFFFF',
                  },
                }}
              >
                Directory
                <ArrowDropDown sx={{ fontSize: 16, ml: 0.25, mr: -0.5 }} />
              </Typography>
              <Menu anchorEl={dirMenuAnchor} open={Boolean(dirMenuAnchor)} onClose={() => setDirMenuAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }} transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                slotProps={{ paper: { sx: { minWidth: 160, mt: 0.5 } } }}>
                {directoryItems.map((item) => (
                  <MenuItem key={item.path} selected={location.pathname.startsWith(item.path)}
                    onClick={() => { setDirMenuAnchor(null); guardedNavigate(item.path); }} sx={{ fontSize: '0.85rem' }}>
                    {item.label}
                  </MenuItem>
                ))}
              </Menu>
            </Box>
          </Box>

          {/* Center: Search Bar (desktop only) */}
          <Box sx={{ flex: 1, display: { xs: 'none', md: 'flex' }, justifyContent: 'center', mx: 2 }}>
            <ClickAwayListener onClickAway={() => setSearchOpen(false)}>
            <Box sx={{ position: 'relative' }}>
            <TextField
              size="small"
              placeholder="Search projects, samples..."
              variant="outlined"
              value={searchValue}
              onChange={(e) => handleSearchChange(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              onFocus={() => { if (searchResults && searchValue.trim().length >= 2) setSearchOpen(true); }}
              inputRef={searchRef}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ color: alpha('#FFFFFF', 0.4), fontSize: 18 }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <Typography
                      component="kbd"
                      sx={{
                        fontSize: '0.6rem',
                        opacity: 0.35,
                        border: `1px solid ${alpha('#FFFFFF', 0.2)}`,
                        px: 0.6,
                        py: 0.1,
                        borderRadius: '3px',
                        color: alpha('#FFFFFF', 0.5),
                        lineHeight: 1.4,
                      }}
                    >
                      Ctrl+K
                    </Typography>
                  </InputAdornment>
                ),
              }}
              sx={{
                width: 340,
                transition: 'width 0.2s ease',
                '&:focus-within': { width: 400 },
                '& .MuiOutlinedInput-root': {
                  backgroundColor: alpha('#FFFFFF', 0.1),
                  borderRadius: '6px',
                  color: '#FFFFFF',
                  fontSize: '0.8rem',
                  height: 34,
                  transition: 'all 0.2s ease',
                  '& fieldset': { borderColor: alpha('#FFFFFF', 0.12) },
                  '&:hover': {
                    backgroundColor: alpha('#FFFFFF', 0.15),
                  },
                  '&:hover fieldset': { borderColor: alpha('#FFFFFF', 0.25) },
                  '&.Mui-focused': {
                    backgroundColor: alpha('#FFFFFF', 0.15),
                  },
                  '&.Mui-focused fieldset': { borderColor: alpha('#FFFFFF', 0.3) },
                },
                '& .MuiOutlinedInput-input::placeholder': {
                  color: alpha('#FFFFFF', 0.45),
                  opacity: 1,
                },
              }}
            />
            {searchResults && (
              <SearchDropdown results={searchResults} open={searchOpen} onClose={() => setSearchOpen(false)} />
            )}
            </Box>
            </ClickAwayListener>
          </Box>

          {/* Moftaki AI — visible only for users with chat access */}
          {user?.canAccessChat && (
            <Tooltip title="Moftaki AI — AI Assistant">
              <Box onClick={() => guardedNavigate(ROUTES.MOFTAKIBOT)}
                sx={{
                  cursor: 'pointer', mr: 1, flexShrink: 0,
                  opacity: location.pathname.startsWith('/moftakibot') ? 1 : 0.75,
                  transition: 'opacity 0.2s, transform 0.2s',
                  '&:hover': { opacity: 1, transform: 'scale(1.08)' },
                }}>
                <MoftakiBotIcon size={38} />
              </Box>
            </Tooltip>
          )}

          {/* Right: User Avatar + Name + Dropdown */}
          <Box onClick={(e) => setUserMenuAnchor(e.currentTarget)}
            sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0, cursor: 'pointer',
              borderRadius: '8px', px: 1, py: 0.5, transition: 'background-color 0.2s ease',
              '&:hover': { backgroundColor: alpha('#FFFFFF', 0.08) } }}>
            <Avatar
              src={user?.photoUrl ?? undefined}
              sx={{ width: 30, height: 30, backgroundColor: '#6B7F68', fontSize: 11,
              fontWeight: 600, border: `2px solid ${alpha('#FFFFFF', 0.25)}` }}>{userInitials}</Avatar>
            <Typography variant="body2" noWrap sx={{ color: alpha('#FFFFFF', 0.9), fontWeight: 500, fontSize: '0.8rem', display: { xs: 'none', sm: 'block' } }}>{userName}</Typography>
            <ArrowDropDown sx={{ color: alpha('#FFFFFF', 0.4), fontSize: 18 }} />
          </Box>
          <Menu anchorEl={userMenuAnchor} open={Boolean(userMenuAnchor)} onClose={() => setUserMenuAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            slotProps={{ paper: { sx: { minWidth: 200, mt: 0.5 } } }}>
            <MenuItem disabled sx={{ opacity: '1 !important', flexDirection: 'column', alignItems: 'flex-start', py: 1 }}>
              <Typography variant="body2" fontWeight={600}>{userName}</Typography>
              <Typography variant="caption" color="text.secondary">{userEmail}</Typography>
            </MenuItem>
            <Divider />
            <MenuItem onClick={() => { setUserMenuAnchor(null); setChangelogOpen(true); }}>About — {APP_VERSION}</MenuItem>
            <MenuItem onClick={() => { setUserMenuAnchor(null); guardedNavigate(ROUTES.HOW_IT_WORKS); }}>✨ How ALIS Works</MenuItem>
            <MenuItem onClick={() => { setUserMenuAnchor(null); guardedNavigate(ROUTES.MY_FEEDBACK); }}>My Feedback</MenuItem>
            {isAdmin && (
              <MenuItem onClick={() => { setUserMenuAnchor(null); guardedNavigate(ROUTES.ADMIN_USERS); }}>Admin</MenuItem>
            )}
            {isSuperAdmin && (
              <>
                <Divider />
                <MenuItem disabled sx={{ fontSize: '0.75rem', opacity: 0.7 }}>Impersonate Role:</MenuItem>
                {(['superadmin', 'admin', 'technician', 'reader'] as AppRole[]).map((r) => (
                  <MenuItem key={r} selected={impersonatingAs === r || (!impersonatingAs && r === 'superadmin')}
                    onClick={() => { setUserMenuAnchor(null); r === 'superadmin' ? stopImpersonation() : startImpersonation(r); }}
                    sx={{ fontSize: '0.8rem', pl: 3 }}>
                    {r === 'superadmin' ? '👑 Super Admin (actual)' : r === 'admin' ? 'Lab Admin' : r === 'technician' ? 'Lab Technician' : 'Lab Reader'}
                  </MenuItem>
                ))}
              </>
            )}
            <Divider />
            <MenuItem onClick={() => { setUserMenuAnchor(null); logout(); }}>Sign Out</MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Impersonation banner */}
      {isSuperAdmin && isImpersonating && (
        <Box sx={{
          position: 'fixed', top: APP_BAR_HEIGHT, left: 0, right: 0, zIndex: 1201,
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5,
          px: 2, py: 0.5,
          background: 'linear-gradient(90deg, #FF8F00, #F57C00)',
          color: '#fff', fontSize: '0.8rem', fontWeight: 600,
        }}>
          <span>👁 Viewing as: {impersonatingAs === 'admin' ? 'Lab Admin' : impersonatingAs === 'technician' ? 'Lab Technician' : 'Lab Reader'}</span>
          <span style={{ fontWeight: 400, opacity: 0.85 }}>— pages, data, and actions behave as this role (edits still recorded under your name)</span>
          <Box component="button" onClick={stopImpersonation}
            sx={{ ml: 1, px: 1.5, py: 0.2, border: '1px solid rgba(255,255,255,0.5)', borderRadius: 1, background: 'transparent', color: '#fff', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 600, '&:hover': { background: 'rgba(255,255,255,0.15)' } }}>
            ✕ Exit
          </Box>
        </Box>
      )}

      {/* Service unavailable banner */}
      <Collapse in={serviceDown.unavailable} sx={{ position: 'fixed', top: APP_BAR_HEIGHT, left: 0, right: 0, zIndex: 1200 }}>
        <Alert severity="warning" icon={<WarningAmber />}
          sx={{ borderRadius: 0, justifyContent: 'center', fontSize: '0.85rem', fontWeight: 500 }}>
          {serviceDown.message || 'Service temporarily unavailable. Please try again later.'}
        </Alert>
      </Collapse>

      {/* Splash screen — cinematic reveal (decode-gated, per-test medallions) */}
      <SplashScreen />

      {/* Main content area */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          mt: `${APP_BAR_HEIGHT}px`,
          px: { xs: 1.5, md: 3 },
          py: 2,
          pb: isMobile ? `${MOBILE_BOTTOM_NAV_HEIGHT + 16}px` : 2,
          backgroundColor: 'background.default',
          minHeight: `calc(100vh - ${APP_BAR_HEIGHT}px)`,
          // 'clip' (not 'hidden') so <main> is NOT a scroll container — page-flow
          // position:sticky elements (table headers under the fixed AppBar) must
          // stick to the WINDOW scroll. 'hidden' silently made <main> the sticky
          // containing scrollport, which never scrolls, so sticky never engaged.
          overflowX: 'clip',
        }}
      >
        <Outlet />
      </Box>

      {isMobile && <MobileBottomNav />}

      <ChangelogDialog open={changelogOpen} onClose={() => setChangelogOpen(false)} />
      <FeedbackFab />
    </Box>
  );
};
